import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  Article,
  ArticleFacetedSearchQuery,
  ArticleFacetedSearchService,
  ArticleFactory,
  Facet,
  FilterService,
  PageMeta,
  Parameter,
  ParameterQuery,
  ProductPageViewFilterService,
  ProductQuery,
  ProductService,
  switchTap,
} from '@lobos/library';
import { combineLatest, iif, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AlternateVersion, AlternateVersionInterface, AlternateVersionService } from '../../../../../services/alternate-version';
import { CatalogHelperService } from '../../../../../services/catalog/catalog-helper.service';
import { ImageHelperService } from '../../../../../services/catalog/image-helper.service';
import { WeylandLocalization } from '../../../../../services/catalog/model/weyland-localization';
import { WeylandProduct } from '../../../../../services/catalog/model/weyland-product';

@AlternateVersion({
  provide: (self: ProductPageComponent) =>
    combineLatest([self.product$, self.parameterQuery.params$]).pipe(
      map(
        ([product, parameter]: [WeylandProduct, Parameter]) =>
          product.oCategoryLocalisations?.map((localization: WeylandLocalization) => ({
            key: localization.sLanguage,
            title: localization.sLanguage.toUpperCase(),
            url: self.catalogHelper.localizedUrlPath(parameter, localization.sUrlPath, localization.sLanguage),
          })) || self.alternateVersionService.getSimple(),
      ),
    ),
})
@PageMeta<ProductPageComponent>({
  provide: (self: ProductPageComponent) =>
    self.product$.pipe(
      map((product: WeylandProduct) => ({
        title: product.sTitle,
        description: product.sDescriptionShort || product.sDescription,
        image: product.oArticles?.length ? self.imageHelperService.getProductMainImage(product)?.toURL() : undefined,
      })),
    ),
})
@Component({
  selector: 'app-product-page',
  templateUrl: './product-page.component.html',
  styleUrls: ['./product-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductPageComponent implements OnInit {
  public product$: Observable<WeylandProduct> = this.activatedRoute.paramMap.pipe(
    map((paramMap: ParamMap) => paramMap.get('id')!),
    switchMap((id: string) =>
      iif(
        () => this.productQuery.hasEntity((entity: WeylandProduct) => entity.Id === id),
        this.productQuery.selectEntity((entity: WeylandProduct) => entity.Id === id),
        this.productService.getById(id).pipe(switchMap((entity: WeylandProduct) => this.productQuery.selectById(entity.Id))),
      ),
    ),
    filter((product: WeylandProduct | undefined) => !!product),
    map((p: WeylandProduct | undefined) => p!),
    tap(() => this.filterService.setFiltersByUrlParams()),
    switchTap((product: WeylandProduct) =>
      this.filterService.selectFilters().pipe(switchMap((filters) => this.articleFacetedSearchService.search(product.sUrlPath, filters))),
    ),
    shareReplay(1),
  );

  public articles$: Observable<Article[]> = this.activatedRoute.paramMap.pipe(
    map((paramMap: ParamMap) => paramMap.get('id')!),
    switchMap((id: string) => this.productQuery.selectById(id)),
    tap(() => this.filterService.setFiltersByUrlParams()),
    switchTap((product: WeylandProduct) =>
      combineLatest([
        this.filterService.selectFilters(),
        this.articleFacetedSearchQuery.pageSize$,
        this.articleFacetedSearchQuery.pageIndex$,
        this.articleFacetedSearchQuery.sortBy$,
        this.articleFacetedSearchQuery.viewMode$,
      ]).pipe(
        switchMap(([filters, pageSize, pageIndex, sortBy, viewMode]) =>
          this.articleFacetedSearchService.searchByFiltersCached(product.sUrlPath, filters, {
            pageSize,
            pageIndex: pageIndex * pageSize,
            sortBy,
            viewMode,
          }),
        ),
      ),
    ),
    switchMap((product: WeylandProduct) => this.articleFacetedSearchQuery.cachedFor(product.sUrlPath)),
    map((articles: Article[]) => articles.map((article: Article) => ArticleFactory.create(article))),
  );

  public totalHits$: Observable<number> = this.activatedRoute.paramMap.pipe(
    map((paramMap: ParamMap) => paramMap.get('id')!),
    switchMap((id: string) => this.productQuery.selectById(id)),
    switchMap((product: WeylandProduct) => this.articleFacetedSearchQuery.cachedTotalFor(product.sUrlPath)),
  );

  public facets$: Observable<Facet[]> = this.articleFacetedSearchQuery.facets$;

  constructor(
    private parameterQuery: ParameterQuery,
    private productService: ProductService,
    private productQuery: ProductQuery,
    private catalogHelper: CatalogHelperService,
    private alternateVersionService: AlternateVersionService<AlternateVersionInterface>,
    private articleFacetedSearchQuery: ArticleFacetedSearchQuery,
    private articleFacetedSearchService: ArticleFacetedSearchService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private imageHelperService: ImageHelperService,
    private productPageViewFilterService: ProductPageViewFilterService,
  ) {}

  ngOnInit() {
    this.filterService.setFiltersByUrlParams();
    this.productPageViewFilterService.resetByUrlParams();
  }
}
